/**
 * App root providers — theme, QueryClient, the router, and the language
 * (I18nProvider: the person's locale, <html lang dir>, and the formatters'
 * defaults). The session is held in memory and restored from the refresh
 * cookie on load (lib/auth/refresh.ts, called from main.tsx); protected
 * screens wait for it (App.tsx RequireAuth).
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/theme";
import { sessionIdentity, useAuthStore } from "@/lib/auth/store";
import { I18nProvider } from "@/lib/i18n";

/**
 * Cached responses belong to the session that fetched them. Drop them on ANY
 * change of session: sign-out, the server ending the session, and a second
 * person signing in from /login while the first is still signed in (D13).
 * Clearing only on "token became empty" missed that last case, and the second
 * person's screens opened on the first person's data.
 *
 * The session, not the token: a refresh replaces the token every 15 minutes
 * and keeps the session (`sid`), and emptying every screen's cache on each
 * one would reload them all (S2-05). sessionIdentity() is the sid, or the
 * token itself for a token that names no session.
 *
 * Returns the unsubscribe.
 */
function clearCacheOnSessionChange(client: QueryClient): () => void {
  return useAuthStore.subscribe((state, prev) => {
    if (sessionIdentity(state) !== sessionIdentity(prev)) client.clear();
  });
}

/**
 * One client for the life of the page (AppProviders is mounted once, in
 * main.tsx). It is bound to the session here, at creation, rather than in an
 * effect, so no token change can land before the binding exists.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: 0 },
  },
});
const unbindCache = clearCacheOnSessionChange(queryClient);
// A hot reload re-runs this module; let the old binding go with the old client.
import.meta.hot?.dispose(unbindCache);

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <I18nProvider>{children}</I18nProvider>
          </BrowserRouter>
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
